import * as React from "react";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { Link } from "react-router-dom";
import { bindActionCreators } from "redux";

import { Alert, Button, Card, Container, Grid, Group, NavLink, Stack, Text, TextInput } from "@mantine/core";

import { savePerson } from "../../data/actions/personActions";
import { IPerson, IPersonUpdate } from "../../types/persons/person";

import { IState } from "../../data/reducers/state";
import { CenteredLoader } from "../common/loader";
import TextSeparator from "../common/TextSeparator";

import CalendarManager from "../calendar/Calendar";
import ErrorBoundary from "../common/ErrorBoundary";
import EmailVerifiedPrompt from "../common/person/EmailVerifiedPrompt";
import PersonalBankDetailsSettings from "./PersonalBankDetailsSettings";
import { PersonalEmailSettings } from "./PersonalEmailSettings";

export interface IPersonalSettingsProps {
  person: IPersonUpdate;
  actions: {
    // savePerson redux action returns a thunk, but when dispatched via bindActionCreators
    // it resolves to a Promise<IPerson>. We'll type it as Promise<any> to avoid coupling.
    savePerson: (person: Partial<IPerson>) => Promise<any>;
  };
}

const PersonalSettings: React.FunctionComponent<IPersonalSettingsProps> = (props) => {
  const [changedPerson, setChangedPerson] = React.useState<Partial<IPerson>>({});
  const [loading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  const person = { ...props.person, ...changedPerson };
  const unsaved = Object.keys(changedPerson).length > 0;

  const original = props.person || ({} as IPerson);

  const onEventChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSuccess(null);
    const { name, value } = e.target;
    // If reverted to original value, remove from changedPerson
    if ((original as any)[name] === value) {
      const clone = { ...changedPerson } as any;
      delete clone[name];
      setChangedPerson(clone);
    } else {
      setChangedPerson({ ...changedPerson, [name]: value });
    }
  };

  const isDirty = (field: keyof IPerson) => Object.prototype.hasOwnProperty.call(changedPerson, field);

  const DirtyWrapper: React.FC<{ dirty: boolean; children: React.ReactNode }> = ({ dirty, children }) => (
    <div style={{ position: 'relative' }}>
      {dirty && (
        <span
          style={{
            position: 'absolute',
            left: 0,
            top: 4,
            bottom: 4,
            width: 4,
            borderRadius: 2,
            background: 'var(--mantine-color-yellow-6)',
          }}
        />
      )}
      <div style={{ paddingLeft: dirty ? 10 : 0 }}>{children}</div>
    </div>
  );

  const saveChanges = () => {
    if (!unsaved) return;
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    props.actions
      .savePerson({ ...changedPerson, _id: person._id })
      .then(() => {
        setIsLoading(false);
        setChangedPerson({});
        setSuccess("Changes saved");
      })
      .catch((err) => {
        setIsLoading(false);
        setError(err.message);
      });
  };

  const resetChanges = () => {
    setChangedPerson({});
    setError(null);
    setSuccess(null);
  };

  const elementDisabled = loading;

  return (
    <Card withBorder radius="md" shadow="sm" padding="lg">
      <Group justify="space-between" align="flex-start" mb="sm">
        <div>
          <h4 style={{ margin: 0 }}>Personal details</h4>
          <Text size="sm" c="dimmed">Update your name and contact information.</Text>
        </div>
        <Group gap="xs">
          {unsaved && (
            <Button variant="light" size="xs" onClick={resetChanges} disabled={loading}>Reset</Button>
          )}
          <Button size="xs" color="blue" onClick={saveChanges} disabled={loading || !unsaved} loading={loading}>Save</Button>
        </Group>
      </Group>
      {unsaved && !loading && (
        <Text size="xs" c="yellow" mb="sm">You have unsaved changes.</Text>
      )}
      {error && <Alert color="red" mb="sm">{error}</Alert>}
      {success && <Alert color="green" mb="sm">{success}</Alert>}
      {loading && <CenteredLoader />}
      <Stack gap="md">
        <Grid gutter="md">
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Stack gap={4}>
              <Text size="xs" fw={600} tt="uppercase" c="dimmed">Identifier</Text>
              <TextInput
                disabled
                name="identifier"
                value={person.externalIdentifier || ''}
              />
              <Text size="xs" c="dimmed">This is the identifier you use to log in.</Text>
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <DirtyWrapper dirty={isDirty('preferredName') as boolean}>
              <Stack gap={4}>
                <Text size="xs" fw={600} tt="uppercase" c="dimmed">Preferred name</Text>
                <TextInput
                  disabled={elementDisabled}
                  name="preferredName"
                  value={person.preferredName || ''}
                  placeholder={person.firstName || ''}
                  onChange={onEventChange}
                />
              </Stack>
            </DirtyWrapper>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <DirtyWrapper dirty={isDirty('firstName') as boolean}>
              <Stack gap={4}>
                <Text size="xs" fw={600} tt="uppercase" c="dimmed">First name</Text>
                <TextInput
                  disabled={elementDisabled}
                  name="firstName"
                  value={person.firstName || ''}
                  onChange={onEventChange}
                />
              </Stack>
            </DirtyWrapper>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <DirtyWrapper dirty={isDirty('lastName') as boolean}>
              <Stack gap={4}>
                <Text size="xs" fw={600} tt="uppercase" c="dimmed">Last name</Text>
                <TextInput
                  disabled={elementDisabled}
                  name="lastName"
                  value={person.lastName || ''}
                  onChange={onEventChange}
                />
              </Stack>
            </DirtyWrapper>
          </Grid.Col>
        </Grid>

        <TextSeparator>Contact details</TextSeparator>
        <Grid gutter="md">
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <DirtyWrapper dirty={isDirty('mobile') as boolean}>
              <Stack gap={4}>
                <Text size="xs" fw={600} tt="uppercase" c="dimmed">Mobile</Text>
                <TextInput
                  disabled={elementDisabled}
                  name="mobile"
                  value={person.mobile || ''}
                  onChange={onEventChange}
                />
                <Text size="xs" c="dimmed"><em>Visible to supervisors & managers of your shifts.</em></Text>
              </Stack>
            </DirtyWrapper>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Stack gap={4}>
              <Text size="xs" fw={600} tt="uppercase" c="dimmed">Email</Text>
              <Text size="xs" c="dimmed" style={{ paddingLeft: 4 }}>
                <em>Manage emails via the <Link to="/settings/email">Email settings</Link> tab.</em>
              </Text>
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>
    </Card>
  );
};

interface SettingsTabMeta {
  id: string;
  title: React.ReactNode; // can include EmailVerifiedPrompt
  description: string;
  icon: string; // font awesome class
  component: any;
}

const PersonalSettingsPage: React.FunctionComponent<IPersonalSettingsProps> = (props) => {
  const navigate = useNavigate();
  const submenu = useParams()?.submenu || "profile";

  React.useEffect(() => {
    document.title = "Stadium Supervisor - Settings";
  }, []);

  const tabs: SettingsTabMeta[] = [
    {
      id: "profile",
      title: "Profile",
      description: "Manage your personal and contact information",
      icon: "fas fa-user",
      component: PersonalSettings,
    },
    {
      id: "email",
      title: <><span style={{ marginRight: 4 }}>Email</span><EmailVerifiedPrompt /></>,
      description: "Manage verified emails & notification preferences",
      icon: "fas fa-envelope",
      component: PersonalEmailSettings,
    },
    {
      id: "payments",
      title: "Bank Details",
      description: "View & manage your bank details for payments",
      icon: "fas fa-university",
      component: PersonalBankDetailsSettings,
    },
    {
      id: "calendar",
      title: "Calendar",
      description: "Sync your schedule with your external calendar",
      icon: "fas fa-calendar-alt",
      component: CalendarManager,
    },
  ];

  const active = tabs.find(t => t.id === submenu) || tabs[0];

  const handleTabClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, tabId: string) => {
    // allow cmd/ctrl click to open new tab
    if (e.metaKey || e.ctrlKey || e.button === 2) return;
    e.preventDefault();
    navigate(`/settings/${tabId}`);
  };

  return (
    <Container fluid py="sm">
      <Grid>
        {/* Sidebar */}
        <Grid.Col span={{ base: 12, sm: 4, md: 3, lg: 2 }} mb="sm">
          <Card
            shadow="sm"
            padding="sm"
            radius="md"
            withBorder
            style={{ position: 'sticky', top: '20px', background: 'var(--color-admin-sidebar)' }}
          >
            <Stack gap="md">
              {/* Header */}
              <div
                style={{
                  padding: '16px',
                  backgroundColor: 'var(--color-admin-elevated)',
                  borderRadius: '8px',
                  border: '1px solid var(--color-table-border)'
                }}
              >
                <Group gap="sm" mb="xs">
                  <i className="fas fa-cog" style={{ color: 'var(--color-button-primary)', fontSize: 18 }} />
                  <h6 style={{ margin: 0, fontWeight: 600 }}>Settings</h6>
                </Group>
                <small style={{ color: 'var(--color-text-medium)' }}>
                  Personal preferences & account configuration
                </small>
              </div>

              {/* Tabs */}
              <div>
                <small
                  style={{
                    textTransform: 'uppercase',
                    color: 'var(--color-text-disabled)',
                    fontWeight: 700,
                    fontSize: '11px',
                    letterSpacing: '0.5px'
                  }}
                >
                  Sections
                </small>
                <Stack gap="xs" mt="sm">
                  {tabs.map(tab => (
                    <NavLink
                      key={tab.id}
                      href={`/settings/${tab.id}`}
                      active={active.id === tab.id}
                      onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => handleTabClick(e, tab.id)}
                      leftSection={<i className={tab.icon} style={{ width: 16, fontSize: 14, color: active.id === tab.id ? 'var(--color-button-primary)' : 'var(--color-text-medium)' }} />}
                      styles={{
                        root: {
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: active.id === tab.id ? 600 : 500,
                          padding: '8px 12px',
                          color: active.id === tab.id ? 'var(--color-button-primary)' : 'var(--color-text-primary)',
                          backgroundColor: active.id === tab.id ? 'var(--color-admin-sidebar-active)' : 'transparent',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'var(--color-admin-sidebar-hover)',
                            color: 'var(--color-text-primary)'
                          }
                        },
                        label: { fontSize: '14px', color: 'inherit' }
                      }}
                      label={tab.title as any}
                    />
                  ))}
                </Stack>
              </div>
            </Stack>
          </Card>
        </Grid.Col>

        {/* Content */}
        <Grid.Col span={{ base: 12, sm: 8, md: 9, lg: 10 }} style={{ flex: 1 }}>
          <Stack gap="md">
            {/* Active Tab Header */}
            <Card shadow="sm" padding="md" radius="md" withBorder>
              <Group>
                <i className={active.icon} style={{ fontSize: 24, color: 'var(--mantine-color-brand-6)' }} />
                <div>
                  <h4 style={{ margin: 0, marginBottom: 4 }}>{active.title}</h4>
                  <p style={{ margin: 0, fontSize: 14 }}>{active.description}</p>
                </div>
              </Group>
            </Card>

            <ErrorBoundary>
              {props.person &&
                React.createElement(active.component, {
                  person: props.person,
                  actions: props.actions,
                })}
            </ErrorBoundary>
          </Stack>
        </Grid.Col>
      </Grid>
    </Container>
  );
};

function mapStateToProps(state: IState, _props: any) {
  return {
    person: state.persons.self && state.persons.map[state.persons.self],
  };
}

function mapDispatchToProps(dispatch: any) {
  return {
    actions: bindActionCreators({ savePerson }, dispatch),
  };
}

// Relax generic typing to avoid thunk vs Promise return mismatch warnings
// Casting connect to any to avoid complex generic constraints mismatching with thunk action types
export default (connect as any)(
  mapStateToProps,
  mapDispatchToProps,
)(PersonalSettingsPage);
